import { AssignStatus } from "@/enum/list.enum";

import { AnalyticsMetrics } from "../schema/analytics";

import { IFilterWithValues } from "../../List/components/FilterModal";

export type KpiKey =
	| "totalLeads"
	| "assignedLeads"
	| "contactedLeads"
	| "endedCampaignLeads"
	| "totalRepliedLeads"
	| "oooReplies"
	| "positiveReplies"
	| "replyInterested"
	| "replyBadTiming"
	| "replyWrongPerson"
	| "replyNotInterested"
	| "replyNeutral";

export const KPI_TO_METRIC_FIELD: Record<KpiKey, keyof AnalyticsMetrics> = {
	totalLeads: "totalLeads",
	assignedLeads: "assignedLeads",
	contactedLeads: "contactedLeads",
	endedCampaignLeads: "endedCampaignLeads",
	totalRepliedLeads: "totalRepliedLeads",
	oooReplies: "totalOooRepliedLeads",
	positiveReplies: "totalPositiveRepliedLeads",
	replyInterested: "totalPositiveRepliedLeads",
	replyBadTiming: "totalBadTimingRepliedLeads",
	replyWrongPerson: "totalWrongPersonRepliedLeads",
	replyNotInterested: "totalNotInterestedRepliedLeads",
	replyNeutral: "totalNeutralRepliedLeads",
};

export const CLICKABLE_KPI_KEYS: KpiKey[] = [
	"totalLeads",
	"assignedLeads",
	"contactedLeads",
	"endedCampaignLeads",
	"totalRepliedLeads",
	"oooReplies",
	"positiveReplies",
	"replyInterested",
	"replyBadTiming",
	"replyWrongPerson",
	"replyNotInterested",
	"replyNeutral",
];

export type KpiDateFilterParam = "list_person.createdAt" | "list_person.addedToCampaignAt";

export interface KpiFilterDescriptor {
	filter: IFilterWithValues;
	dateFilterParam: KpiDateFilterParam | null;
}

const DATE_CREATED_AT: KpiDateFilterParam = "list_person.createdAt";
const DATE_ADDED_TO_CAMPAIGN_AT: KpiDateFilterParam = "list_person.addedToCampaignAt";

const sentimentItem = (id: string, title: string) => ({ id, title });

// The Analytics "Assigned" tile counts `assignedLeads` = the narrow campaign metric
// (backend ASSIGNED_LEAD_STATUSES, incl. ENDED since PREC-3568). The drill-down must use the SAME
// set so the tile number and the contacts list it opens agree (PREC-3541) — not the broader
// list-detail "Assigned" tab set.
const analyticsAssignedStatuses = [
	AssignStatus.ASSIGNED,
	AssignStatus.RUNNING,
	AssignStatus.DONE,
	AssignStatus.MESSAGE_SENT,
	AssignStatus.REPLIES,
	AssignStatus.ENDED,
];

// "Contacted" KPI is conversation-based (>=1 outbound campaign send). There is no exact contacts
// filter for that, so the drill-down approximates it with the assigned statuses that imply a send
// went out — everything past ASSIGNED (which is only queued). Counts may differ slightly from the
// KPI number for this row alone.
const contactedDrillStatuses = [
	AssignStatus.RUNNING,
	AssignStatus.MESSAGE_SENT,
	AssignStatus.REPLIES,
	AssignStatus.ENDED,
	AssignStatus.DONE,
];

// Mirrors the backend ENDED_CAMPAIGN_LEAD_STATUSES so the tile and its drill-down agree exactly.
const endedCampaignDrillStatuses = [
	AssignStatus.ENDED,
	AssignStatus.MESSAGE_SENT,
	AssignStatus.REPLIES,
	AssignStatus.DONE,
];

const assignStatusFilter = (statuses: AssignStatus[]): IFilterWithValues => ({
	lastAssignStatus: {
		value: statuses.map((status) => ({ id: status, title: status })),
		type: "dropdown",
		filterParam: "list_person.assignStatus",
	},
});

/**
 * "Has replied at all", the exact population the `totalRepliedLeads` tile counts
 * (`conv.hasAnyReply = 1`). Enumerating the five user-assignable sentiments instead silently
 * dropped out-of-office, meaningless, unsubscribed and still-unclassified replies — a quarter of
 * all replied leads, and every one of them on the campaign PREC-3909 was reported against.
 */
const hasRepliedFilter = (): IFilterWithValues => ({
	hasAnyReply: {
		type: "isNotEmpty",
		filterParam: "conversation.hasAnyReply",
	},
});

const bestSentimentFilter = (items: { id: string; title: string }[]): IFilterWithValues => ({
	bestSentiment: {
		value: items,
		type: "dropdown",
		filterParam: "conversation.bestSentiment",
	},
});

export const KPI_FILTER_MAP: Record<KpiKey, KpiFilterDescriptor> = {
	totalLeads: {
		filter: {},
		dateFilterParam: DATE_CREATED_AT,
	},
	assignedLeads: {
		filter: assignStatusFilter(analyticsAssignedStatuses),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	contactedLeads: {
		filter: assignStatusFilter(contactedDrillStatuses),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	endedCampaignLeads: {
		filter: assignStatusFilter(endedCampaignDrillStatuses),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	totalRepliedLeads: {
		filter: hasRepliedFilter(),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	// The out-of-office row was inert until PREC-3909 because no KPI key described it. The tile
	// counts `best_sent.bestSentiment = 'OUT_OF_OFFICE'` (analytics-query.builder), so the
	// drill-down reads the same dimension and the opened table matches the number clicked.
	oooReplies: {
		filter: bestSentimentFilter([sentimentItem("OUT_OF_OFFICE", "Out of Office")]),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	positiveReplies: {
		filter: bestSentimentFilter([sentimentItem("INTERESTED", "Interested")]),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	replyInterested: {
		filter: bestSentimentFilter([sentimentItem("INTERESTED", "Interested")]),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	// The per-sentiment breakdown KPIs are computed in analytics from `best_sent.bestSentiment`
	// (analytics-query.builder), so their drill-downs must filter on bestSentiment too — filtering
	// on lastSentiment counted a different population (best-vs-last mismatch, PREC-3541).
	replyBadTiming: {
		filter: bestSentimentFilter([sentimentItem("BAD_TIMING", "Bad Timing")]),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	replyWrongPerson: {
		filter: bestSentimentFilter([sentimentItem("WRONG_PERSON", "Wrong Person")]),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	replyNotInterested: {
		filter: bestSentimentFilter([sentimentItem("NOT_INTERESTED", "Not Interested")]),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
	replyNeutral: {
		filter: bestSentimentFilter([sentimentItem("NEUTRAL", "Neutral")]),
		dateFilterParam: DATE_ADDED_TO_CAMPAIGN_AT,
	},
};
